/**
 * Centralized owner dashboard — cross-branch roll-ups (ML-02/ML-03/ML-04).
 * Consumes the v_multilocation_occupancy + v_multilocation_revenue views via the
 * api-client and composes a per-branch + tenant-total summary. Owner-only route.
 */
import {
  composeMultiLocationSummary,
  getMultiLocationOccupancy,
  getMultiLocationRevenue,
} from '@gym/api-client';
import { useQuery } from '@tanstack/react-query';
import { Building2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { CenterSpinner, EmptyState, PageHeader } from '../../components/ui.js';
import { useAuth } from '../../lib/auth.js';
import { supabase } from '../../lib/supabase.js';

import { CrossBranchStats } from './CrossBranchStats.js';
import { LocationCard } from './LocationCard.js';

export function BranchOverview(): ReactNode {
  const { t } = useTranslation();
  const { claims, hasRole } = useAuth();
  const tenantId = claims?.tenant_id ?? '';
  // Cross-branch analytics is owner-only (plan 12 Task 11). Defence-in-depth on
  // top of the router role-gate so a manager/front_desk never sees roll-ups.
  const isOwner = hasRole('owner');

  const query = useQuery({
    queryKey: ['multilocation_summary', tenantId],
    enabled: !!tenantId && isOwner,
    queryFn: async () => {
      const [occupancy, revenue] = await Promise.all([
        getMultiLocationOccupancy(supabase, tenantId),
        getMultiLocationRevenue(supabase, tenantId),
      ]);
      return composeMultiLocationSummary(tenantId, occupancy, revenue);
    },
  });

  const summary = query.data;

  if (!isOwner) {
    return (
      <div className="grid gap-5">
        <PageHeader title={t('multilocation.title')} />
        <EmptyState icon={Building2} title={t('multilocation.ownerOnly')} />
      </div>
    );
  }

  return (
    <div className="grid gap-5">
      <PageHeader title={t('multilocation.title')} subtitle={t('multilocation.branches')} />
      {query.isLoading ? (
        <CenterSpinner />
      ) : !summary || summary.totals.locations_count === 0 ? (
        <EmptyState icon={Building2} title={t('multilocation.empty')} />
      ) : (
        <>
          <CrossBranchStats totals={summary.totals} />
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {summary.locations.map((loc) => (
              <LocationCard key={loc.location_id} location={loc} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
